import React from 'react';
import { View, ViewStyle } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import {
  iconTokens,
  IconSize,
  IconVariant,
  IconName,
} from '../../tokens/icons';
import { colorTokens } from '../../tokens/colors';
import { useTheme } from '../../hooks/useTheme';

// SVG path definitions for common icons
const iconPaths: Record<string, string> = {
  // Navigation icons
  home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  profile:
    'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  menu: 'M4 6h16M4 12h16M4 18h16',
  back: 'M15 19l-7-7 7-7',
  close: 'M6 18L18 6M6 6l12 12',
  x: 'M6 18L18 6M6 6l12 12',

  // Action icons
  heart:
    'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z',
  bookmark: 'M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z',
  share:
    'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z',
  edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  delete:
    'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  add: 'M12 6v6m0 0v6m0-6h6m-6 0H6',

  // Status icons
  check: 'M5 13l4 4L19 7',
  warning:
    'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.34 16.5c-.77.833.192 2.5 1.732 2.5z',
  error: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',

  // Coffee-specific icons
  coffee:
    'M5 11h14l-1 7a2 2 0 01-2 2H8a2 2 0 01-2-2l-1-7zM6 11V9a4 4 0 118 0v2m-4-8v2',
  timer: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  temperature:
    'M8 14a2 2 0 104 0c0 1.097-.39 2.037-1 2.828V18a1 1 0 11-2 0v-1.172A2.99 2.99 0 018 14zm2-6a2 2 0 00-2 2v.5M10 8V6a2 2 0 114 0v2',
  grinder: 'M8 2v4m8-4v4M6 6h12l-1 12a2 2 0 01-2 2H9a2 2 0 01-2-2L6 6zm6 4v8',
  scale: 'M3 18h18l-9-14L3 18zm9-10v6m-6 4h12',

  // Media icons
  play: 'M8 5v14l11-7z',
  pause: 'M10 9v6m4-6v6',
  camera:
    'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0017.07 7H18a2 2 0 012 2v9a2 2 0 01-2 2H6a2 2 0 01-2-2V9z M15 13a3 3 0 11-6 0 3 3 0 016 0z',
  image:
    'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  video:
    'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',

  // Interface icons
  'chevron-up': 'M7 14l5-5 5 5',
  'chevron-down': 'M7 10l5 5 5-5',
  'chevron-left': 'M15 18l-6-6 6-6',
  'chevron-right': 'M9 18l6-6-6-6',
  dots: 'M12 5v.01M12 12v.01M12 19v.01',
  filter:
    'M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z',
  sort: 'M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12',

  // Theme and settings icons
  sun: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
  moon: 'M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z',
  settings:
    'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',

  // Seasonal icons
  snowflake:
    'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
  flower:
    'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
  leaf: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',

  // Custom theme icons
  circle:
    'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z',
  fire: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
  droplet:
    'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
};

// Extended icon name type that includes both camelCase and kebab-case names
type ExtendedIconName =
  | IconName
  | 'x'
  | 'sun'
  | 'moon'
  | 'settings'
  | 'snowflake'
  | 'flower'
  | 'leaf'
  | 'circle'
  | 'fire'
  | 'droplet'
  | 'chevron-up'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right';

export interface IconProps {
  /** Icon name from the predefined set */
  name: ExtendedIconName;
  /** Icon size */
  size?: IconSize | number;
  /** Icon variant */
  variant?: IconVariant;
  /** Custom color (overrides theme color) */
  color?: string;
  /** Additional styles */
  style?: ViewStyle;
  /** Test ID for testing */
  testID?: string;
}

/**
 * Icon component with Apple-inspired design and Nothing Technology's geometric precision
 * Provides consistent iconography across the design system
 */
export function Icon({
  name,
  size = 'md',
  variant = 'outline',
  color,
  style,
  testID,
}: IconProps) {
  const { theme } = useTheme();

  // Handle both string and number sizes
  const iconSize = typeof size === 'number' ? size : iconTokens.size[size];
  const variantConfig = iconTokens.variants[variant];

  // Determine icon color
  const iconColor = color || theme.colors.text.primary;

  // Get SVG path for the icon
  const pathData = iconPaths[name];

  if (!pathData) {
    console.warn(
      `Icon "${name}" not found. Available icons:`,
      Object.keys(iconPaths)
    );
    return null;
  }

  const containerStyle: ViewStyle = {
    width: iconSize,
    height: iconSize,
    alignItems: 'center',
    justifyContent: 'center',
    ...style,
  };

  return (
    <View style={containerStyle} testID={testID}>
      <Svg
        width={iconSize}
        height={iconSize}
        viewBox='0 0 24 24'
        fill='none'
        stroke={iconColor}
        strokeWidth={variantConfig.strokeWidth === 'none' ? 0 : 2}
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <Path
          d={pathData}
          fill={variantConfig.fill === 'none' ? 'none' : iconColor}
          opacity={
            variant === 'duotone' && 'opacity' in variantConfig
              ? variantConfig.opacity?.primary
              : 1
          }
        />
      </Svg>
    </View>
  );
}

// Export icon component with display name for debugging
Icon.displayName = 'Icon';
